import useOperationProductApis from 'api/operation/product/useOperationProductApis';
import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

import useProductSearchPermissions from './useProductSearchPermissions';

const PAGE_SIZE = 50;

const INITIAL_ADD = {
  open: false,
  step: 'search',
  searchTerm: '',
  searchResults: [],
  searchLoading: false,
  packages: [],
  selectedProduct: null,
  selectedPack: null,
  quantity: '1',
};

const INITIAL_EDIT = {
  item: null,
  qty: '1',
  packOptions: [],
};

const INITIAL_REPORTS = {
  item: null,
  step: 'list',
  list: [],
  form: { title: '', description: '', quantity: '' },
  editing: null,
};

// eslint-disable-next-line max-statements
const useProductSearch = ({
  operationGuid,
  state,
  voucherType,
  features,
  onChanged,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = operationSettings?.defaultZone?.key;
  const {
    getOperationProducts,
    searchProducts,
    getProductPackages,
    insertOperationProduct,
    updateOperationProductQuantity,
    deleteOperationProduct,
    getPackUpdateOptions,
    updateOperationProductPack,
    getProductReports,
    insertProductReport,
    getProductReport,
    updateProductReport,
    deleteProductReport,
  } = useOperationProductApis();

  const permissions = useProductSearchPermissions({
    state,
    voucherType,
    features,
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(() =>
    Boolean(operationGuid),
  );
  const [products, setProducts] = useState([]);
  const [orderedProductList, setOrderedProductList] = useState({});
  const [listTerm, setListTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [add, setAdd] = useState(INITIAL_ADD);
  const [edit, setEdit] = useState(INITIAL_EDIT);
  const [reports, setReports] = useState(INITIAL_REPORTS);

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadProducts = useCallback(
    async ({ term = listTerm, pageNumber = 1 } = {}) => {
      if (!operationGuid) return;
      setLoading(true);
      try {
        const res = await getOperationProducts({
          operationGuid,
          term,
          page: pageNumber,
          count: PAGE_SIZE,
        });
        if (!res) return;

        const pageKey = res.pageID ?? pageNumber;
        const orderedList = (res.list ?? []).map(element => ({
          id: element.guid,
          code: element.code,
          guid: element.guid,
          imageUrl: element.imageUrl,
          mapCode: element.mapCode,
          packDisplay: element.packDisplay,
          problemQuantity: element.problemQuantity,
          quantity: element.quantity,
          title: element.title,
        }));

        setOrderedProductList(prev => {
          const previousList = prev[pageKey];
          if (
            previousList &&
            previousList.length === orderedList.length &&
            previousList.every(
              (item, index) =>
                item.id === orderedList[index].id &&
                item.guid === orderedList[index].guid &&
                item.quantity === orderedList[index].quantity &&
                item.problemQuantity === orderedList[index].problemQuantity &&
                item.packDisplay === orderedList[index].packDisplay &&
                item.title === orderedList[index].title &&
                item.code === orderedList[index].code &&
                item.mapCode === orderedList[index].mapCode &&
                item.imageUrl === orderedList[index].imageUrl,
            )
          ) {
            return prev;
          }

          return { ...prev, [pageKey]: orderedList };
        });
        setProducts(res.list ?? []);
        setTotalCount(res.totalCount ?? 0);
        setPage(pageKey);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [getOperationProducts, listTerm, operationGuid],
  );

  useEffect(() => {
    if (operationGuid) {
      setInitialLoading(true);
      loadProducts({ term: '', pageNumber: 1 });
      setListTerm('');
    } else {
      setInitialLoading(false);
    }
    // intentionally only when modal opens / operation changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationGuid]);

  useEffect(() => {
    if (!add.open || !add.searchTerm.trim()) {
      setAdd(prev =>
        prev.searchResults.length === 0 ? prev : { ...prev, searchResults: [] },
      );
      return undefined;
    }
    const timer = setTimeout(async () => {
      setAdd(prev => ({ ...prev, searchLoading: true }));
      const res = await searchProducts({
        zoneGuid,
        term: add.searchTerm,
        page: 1,
        count: 70,
      });
      setAdd(prev => ({
        ...prev,
        searchLoading: false,
        searchResults: res
          ? Array.isArray(res)
            ? res
            : res.list ?? []
          : prev.searchResults,
      }));
    }, 300);
    return () => clearTimeout(timer);
  }, [add.open, add.searchTerm, searchProducts, zoneGuid]);

  const notifyChanged = async () => {
    await loadProducts();
    onChanged?.();
    window.dispatchEvent(new Event('refreshOperationList'));
  };

  const openAdd = () => {
    setAdd({ ...INITIAL_ADD, open: true });
  };

  const handleSelectSearchProduct = async product => {
    setAdd(prev => ({ ...prev, selectedProduct: product }));
    setLoading(true);
    const packs = await getProductPackages({
      productGuid: product.guid,
      zoneGuid,
    });
    setLoading(false);
    setAdd(prev => ({
      ...prev,
      packages: Array.isArray(packs) ? packs : packs?.list ?? [],
      step: 'pack',
    }));
  };

  const handleSelectPack = pack => {
    setAdd(prev => ({ ...prev, selectedPack: pack, step: 'qty' }));
  };

  const handleInsert = async () => {
    const qty = Number.parseInt(add.quantity, 10);
    if (!add.selectedPack?.guid || !Number.isFinite(qty) || qty <= 0) return;
    setLoading(true);

    const res = await insertOperationProduct({
      operationGuid,
      productPackGuid: add.selectedPack.guid,
      quantity: qty,
    });

    setLoading(false);

    if (res.status === 200) {
      setAdd(INITIAL_ADD);
      await notifyChanged();
    }
  };

  const openEdit = async item => {
    setEdit({ item, qty: String(item.quantity ?? 1), packOptions: [] });
    if (permissions.canEditPack) {
      setLoading(true);
      const packs = await getPackUpdateOptions(item.guid);
      setLoading(false);
      setEdit(prev => ({
        ...prev,
        packOptions: Array.isArray(packs) ? packs : packs?.list ?? [],
      }));
    }
  };

  const saveEdit = async () => {
    if (!edit.item) return;
    setLoading(true);
    let ok = null;
    if (permissions.canEditPack) {
      ok = true;
    } else {
      const qty = Number.parseInt(edit.qty, 10);
      if (!Number.isFinite(qty) || qty <= 0) {
        setLoading(false);
        return;
      }
      ok = await updateOperationProductQuantity({
        guid: edit.item.guid,
        quantity: qty,
      });
    }
    setLoading(false);
    if (ok && !permissions.canEditPack) {
      setEdit(INITIAL_EDIT);
      await notifyChanged();
    }
  };

  const savePackEdit = async pack => {
    if (!edit.item) return;
    setLoading(true);
    const ok = await updateOperationProductPack({
      operationProductGuid: edit.item.guid,
      packGuid: pack.guid,
    });
    setLoading(false);
    if (ok) {
      setEdit(INITIAL_EDIT);
      await notifyChanged();
    }
  };

  const handleDelete = async guid => {
    if (!guid) return;

    setLoading(true);
    const ok = await deleteOperationProduct(guid);
    setLoading(false);
    if (ok) await notifyChanged();
    return ok;
  };

  const openReports = async item => {
    setReports({
      ...INITIAL_REPORTS,
      item,
    });
    setLoading(true);
    const res = await getProductReports(item.guid);
    setLoading(false);
    setReports(prev => ({
      ...prev,
      list: Array.isArray(res) ? res : res?.list ?? [],
    }));
  };

  const startCreateReport = () => {
    setReports(prev => ({
      ...prev,
      step: 'form',
      editing: null,
      form: INITIAL_REPORTS.form,
    }));
  };

  const startEditReport = async report => {
    setReports(prev => ({ ...prev, step: 'form', editing: report }));
    const detail = await getProductReport(report.guid);
    setReports(prev => ({
      ...prev,
      form: {
        title: detail?.title ?? report.title ?? '',
        description: detail?.description ?? '',
        quantity: String(detail?.quantity ?? report.quantity ?? ''),
      },
    }));
  };

  const backToReportsList = () => {
    setReports(prev => ({
      ...prev,
      step: 'list',
      editing: null,
      form: INITIAL_REPORTS.form,
    }));
  };

  const saveReport = async () => {
    if (!reports.item) return;
    setLoading(true);
    let ok;
    if (reports.editing) {
      ok = await updateProductReport({
        guid: reports.editing.guid,
        ...reports.form,
        quantity: Number(reports.form.quantity) || 0,
      });
    } else {
      ok = await insertProductReport({
        operationProductGuid: reports.item.guid,
        ...reports.form,
        quantity: Number(reports.form.quantity) || 0,
      });
    }
    setLoading(false);
    if (ok) {
      const res = await getProductReports(reports.item.guid);
      setReports(prev => ({
        ...prev,
        step: 'list',
        editing: null,
        form: INITIAL_REPORTS.form,
        list: Array.isArray(res) ? res : res?.list ?? [],
      }));
      await notifyChanged();
    }
  };

  const deleteReport = async report => {
    setLoading(true);
    const ok = await deleteProductReport(report.guid);
    setLoading(false);
    if (ok) {
      const res = await getProductReports(reports.item.guid);
      setReports(prev => ({
        ...prev,
        list: Array.isArray(res) ? res : res?.list ?? [],
      }));
      await notifyChanged();
    }
  };

  return {
    ...permissions,
    loading,
    initialLoading,
    products,
    orderedProductList,
    listTerm,
    setListTerm,
    page,
    pageCount,
    loadProducts,
    openAdd,
    handleDelete,
    openEdit,
    openReports,
    add: {
      ...add,
      onClose: () => setAdd(prev => ({ ...prev, open: false })),
      setStep: step => setAdd(prev => ({ ...prev, step })),
      setSearchTerm: searchTerm => setAdd(prev => ({ ...prev, searchTerm })),
      setQuantity: quantity => setAdd(prev => ({ ...prev, quantity })),
      onSelectProduct: handleSelectSearchProduct,
      onSelectPack: handleSelectPack,
      onInsert: handleInsert,
    },
    edit: {
      ...edit,
      onClose: () => setEdit(INITIAL_EDIT),
      setQty: qty => setEdit(prev => ({ ...prev, qty })),
      onSave: saveEdit,
      onSavePack: savePackEdit,
    },
    reports: {
      ...reports,
      onClose: () => setReports(INITIAL_REPORTS),
      setForm: updater =>
        setReports(prev => ({
          ...prev,
          form: typeof updater === 'function' ? updater(prev.form) : updater,
        })),
      onStartCreate: startCreateReport,
      onStartEdit: startEditReport,
      onBackToList: backToReportsList,
      onSave: saveReport,
      onDelete: deleteReport,
    },
  };
};

export default useProductSearch;
